/**
 * As regras da aba Financeiro, portadas do legado como funções puras (sem tela, sem banco).
 *
 * POR QUE PURAS: no legado elas leem os filtros direto do DOM (`lerFiltrosDatas`, `admin.html:8172`)
 * e escrevem HTML. Aqui recebem os valores e devolvem dados, e o resultado é o mesmo: o mesmo
 * plano entra ou sai do filtro, o mesmo total aparece. A diferença é que a regra pode ser provada
 * sem uma tela aberta.
 *
 * NENHUMA REGRA FOI CORRIGIDA. Onde o legado tem comportamento estranho, ele está aqui igual, com o
 * comentário dizendo o que é.
 */

// As linhas do banco que o Financeiro lê.
import type {
  LinhaCuponsDescontoResgates,
  LinhaGiftCards,
  LinhaLeadsFunil,
  LinhaPagamentos,
  LinhaParcelas,
} from '@studiolu/dados';
// A moeda idêntica à do legado.
import { formatarMoeda } from '../../comum/formatos.js';

/** Um plano de pagamento com o lead e as parcelas junto, como o `dadosFinanceiro` do legado (`admin.html:8152`). */
export interface PlanoFinanceiro extends LinhaPagamentos {
  // O lead vinculado; `undefined` quando o lead não está entre os carregados, como o `.find` do legado.
  readonly lead: LinhaLeadsFunil | undefined;
  readonly parcelas: readonly LinhaParcelas[];
}

/** As formas de pagamento e o rótulo de cada uma (`FORMAS_PAGAMENTO`, `admin.html:7806`). */
export const FORMAS_PAGAMENTO: Readonly<Record<string, string>> = Object.freeze({
  pix: 'PIX',
  cartao_credito: 'Cartão de crédito',
  transferencia: 'Transferência bancária',
  boleto: 'Boleto',
  dinheiro: 'Dinheiro',
});

/** As opções de forma na ORDEM dos `<select>` do legado (`admin.html:1880` e `2938`), que é alfabética, e não a do mapa. */
export const OPCOES_FORMA = Object.freeze([
  { valor: 'boleto', rotulo: 'Boleto' },
  { valor: 'cartao_credito', rotulo: 'Cartão de crédito' },
  { valor: 'dinheiro', rotulo: 'Dinheiro' },
  { valor: 'pix', rotulo: 'PIX' },
  { valor: 'transferencia', rotulo: 'Transferência bancária' },
] as const);

/** Os meses por extenso, para o relatório por mês (`MESES_NOMES`, `admin.html:7814`). */
export const MESES_NOMES = Object.freeze([
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]);

/* ---------------------------------------------------------------------------------------------
 * STATUS DA PARCELA
 * ------------------------------------------------------------------------------------------- */

/** Os três estados de uma parcela. */
export type StatusParcela = 'pago' | 'atrasada' | 'pendente';

/** O rótulo de cada estado, como o selo do legado escreve (`admin.html:8168`). */
export const ROTULO_STATUS_PARCELA: Readonly<Record<StatusParcela, string>> = Object.freeze({
  pago: 'Pago',
  atrasada: 'Atrasada',
  pendente: 'Pendente',
});

/**
 * O estado de uma parcela (`getStatusParcela`, `admin.html:8160`): paga é "pago"; vencida ANTES de
 * hoje é "atrasada"; o resto é "pendente". O vencimento é lido à meia-noite local, e hoje também.
 */
export function statusParcela(parcela: Pick<LinhaParcelas, 'pago' | 'data_vencimento'>, agora: Date = new Date()): StatusParcela {
  if (parcela.pago) return 'pago';
  // Hoje à meia-noite: a parcela que vence hoje ainda não está atrasada.
  const hoje = new Date(agora);
  hoje.setHours(0, 0, 0, 0);
  return new Date(parcela.data_vencimento + 'T00:00:00') < hoje ? 'atrasada' : 'pendente';
}

/* ---------------------------------------------------------------------------------------------
 * FILTROS
 * ------------------------------------------------------------------------------------------- */

/** Os cinco campos de filtro, como texto, do jeito que os campos da tela guardam. */
export interface FiltrosFinanceiro {
  readonly dataIni: string;
  readonly dataFim: string;
  readonly status: string;
  readonly forma: string;
  readonly cliente: string;
}

/** Os filtros zerados, como o "Limpar" deixa (`admin.html:8589`). */
export const FILTROS_VAZIOS: FiltrosFinanceiro = Object.freeze({ dataIni: '', dataFim: '', status: '', forma: '', cliente: '' });

/**
 * Os limites de data (`lerFiltrosDatas`, `admin.html:8172`): o início à meia-noite local e o FIM
 * ÀS 23:59:59, para o último dia inteiro entrar.
 */
export function limitesDeData(filtros: FiltrosFinanceiro): { readonly dataIni: Date | null; readonly dataFim: Date | null } {
  return {
    dataIni: filtros.dataIni ? new Date(filtros.dataIni + 'T00:00:00') : null,
    dataFim: filtros.dataFim ? new Date(filtros.dataFim + 'T23:59:59') : null,
  };
}

/** A parcela vence dentro do intervalo? Sem intervalo, sempre (`parcelaNoRange`, `admin.html:8183`). */
export function parcelaNoIntervalo(parcela: Pick<LinhaParcelas, 'data_vencimento'>, dataIni: Date | null, dataFim: Date | null): boolean {
  if (!dataIni && !dataFim) return true;
  const d = new Date(parcela.data_vencimento + 'T00:00:00');
  if (dataIni && d < dataIni) return false;
  if (dataFim && d > dataFim) return false;
  return true;
}

/**
 * Os planos que passam no filtro (`filtrarDadosFinanceiro`, `admin.html:8191`): forma EXATA; cliente
 * "contém", sem caixa, no nome do lead; e data e status mantêm o plano quando ALGUMA parcela casa.
 */
export function filtrarPlanos(
  planos: readonly PlanoFinanceiro[],
  filtros: FiltrosFinanceiro,
  agora: Date = new Date(),
): PlanoFinanceiro[] {
  const { dataIni, dataFim } = limitesDeData(filtros);
  // O cliente é comparado em minúsculas e aparado, como o `.toLowerCase().trim()` do legado.
  const clienteF = filtros.cliente.toLowerCase().trim();
  return planos.filter((pag) => {
    if (filtros.forma && pag.forma_pagamento !== filtros.forma) return false;
    if (clienteF && !(pag.lead?.nome || '').toLowerCase().includes(clienteF)) return false;
    if (!dataIni && !dataFim && !filtros.status) return true;
    return pag.parcelas.some((p) => {
      if (!parcelaNoIntervalo(p, dataIni, dataFim)) return false;
      if (filtros.status && statusParcela(p, agora) !== filtros.status) return false;
      return true;
    });
  });
}

/**
 * Os gift cards que passam no filtro (`filtrarGiftCardsFinanceiro`, `admin.html:8210`): pela data de
 * EMISSÃO (hora completa, comparada direto com os limites) e pelo nome de quem compra OU recebe.
 * O filtro de status e o de forma não valem aqui, como no legado.
 */
export function filtrarGiftCards(giftCards: readonly LinhaGiftCards[], filtros: FiltrosFinanceiro): LinhaGiftCards[] {
  const { dataIni, dataFim } = limitesDeData(filtros);
  const clienteF = filtros.cliente.toLowerCase().trim();
  return giftCards.filter((gc) => {
    const emitido = new Date(gc.emitido_em);
    if (dataIni && emitido < dataIni) return false;
    if (dataFim && emitido > dataFim) return false;
    if (clienteF) {
      const comprador = (gc.nome_comprador || '').toLowerCase();
      const destinatario = (gc.nome_destinatario || '').toLowerCase();
      if (!comprador.includes(clienteF) && !destinatario.includes(clienteF)) return false;
    }
    return true;
  });
}

/** O nome do lead de um id, ou `undefined`, pela comparação em texto do legado (`String(l.id) === String(...)`). */
export function nomeDoLead(leads: readonly LinhaLeadsFunil[], leadId: number | null): string | undefined {
  return leads.find((l) => String(l.id) === String(leadId))?.nome;
}

/**
 * Os cupons aplicados que passam no filtro (`filtrarCuponsFinanceiro`, `admin.html:8226`): pela data
 * de APLICAÇÃO e pelo nome do lead.
 */
export function filtrarCupons(
  cupons: readonly LinhaCuponsDescontoResgates[],
  filtros: FiltrosFinanceiro,
  leads: readonly LinhaLeadsFunil[],
): LinhaCuponsDescontoResgates[] {
  const { dataIni, dataFim } = limitesDeData(filtros);
  const clienteF = filtros.cliente.toLowerCase().trim();
  return cupons.filter((r) => {
    // `aplicado_em` nunca é nulo aqui: a consulta só traz os aplicados.
    const aplicado = new Date(r.aplicado_em ?? '');
    if (dataIni && aplicado < dataIni) return false;
    if (dataFim && aplicado > dataFim) return false;
    if (clienteF) {
      const leadNome = (nomeDoLead(leads, r.lead_id) || '').toLowerCase();
      if (!leadNome.includes(clienteF)) return false;
    }
    return true;
  });
}

/* ---------------------------------------------------------------------------------------------
 * TOTAIS
 * ------------------------------------------------------------------------------------------- */

/** Recebido e a receber de um conjunto de parcelas, só as que vencem no intervalo. */
export function somarParcelas(
  parcelas: readonly Pick<LinhaParcelas, 'pago' | 'valor' | 'data_vencimento'>[],
  dataIni: Date | null,
  dataFim: Date | null,
): { recebido: number; aReceber: number } {
  let recebido = 0;
  let aReceber = 0;
  parcelas.forEach((p) => {
    if (!parcelaNoIntervalo(p, dataIni, dataFim)) return;
    if (p.pago) recebido += p.valor;
    else aReceber += p.valor;
  });
  return { recebido, aReceber };
}

/** Os quatro números do resumo, calculados como `renderizarFinanceiro` (`admin.html:8252` a `8262`). */
export interface TotaisDoResumo {
  // Parcelas pagas no período MAIS os gift cards emitidos no período.
  readonly recebido: number;
  readonly aReceber: number;
  readonly descontos: number;
}

export function totaisDoResumo(
  planos: readonly PlanoFinanceiro[],
  giftCards: readonly LinhaGiftCards[],
  cupons: readonly LinhaCuponsDescontoResgates[],
  filtros: FiltrosFinanceiro,
): TotaisDoResumo {
  const { dataIni, dataFim } = limitesDeData(filtros);
  let recebidoPlanos = 0;
  let aReceber = 0;
  planos.forEach((pag) => {
    const soma = somarParcelas(pag.parcelas, dataIni, dataFim);
    recebidoPlanos += soma.recebido;
    aReceber += soma.aReceber;
  });
  // `Number(...)`: o legado converte, porque o valor do gift card e o do cupom podem vir como texto.
  const totalGiftCards = giftCards.reduce((s, gc) => s + Number(gc.valor), 0);
  const totalCupons = cupons.reduce((s, r) => s + Number(r.valor_aplicado || 0), 0);
  return { recebido: recebidoPlanos + totalGiftCards, aReceber, descontos: totalCupons };
}

/* ---------------------------------------------------------------------------------------------
 * GIFT CARD
 * ------------------------------------------------------------------------------------------- */

/**
 * O texto da situação de um gift card (`statusGiftCard`, `admin.html:7164`): "Resgatado em
 * dd/mm/aaaa · Nome do lead", "Expirado" ou "Ativo". A comparação do lead é ESTRITA, como no legado.
 */
export function textoDoGiftCard(gc: LinhaGiftCards, leads: readonly LinhaLeadsFunil[], agora: Date = new Date()): string {
  if (gc.resgatado_em) {
    const lead = leads.find((l) => l.id === gc.lead_id_resgate);
    const dataResgate = new Date(gc.resgatado_em).toLocaleDateString('pt-BR');
    return `Resgatado em ${dataResgate}${lead ? ' · ' + (lead.nome || '(sem nome)') : ''}`;
  }
  if (gc.validade && new Date(gc.validade + 'T00:00:00') < agora) return 'Expirado';
  return 'Ativo';
}

/** O selo do gift card no Financeiro (`admin.html:8289`): resgatado é "pago", vencido "atrasada", o resto "pendente". */
export function seloDoGiftCard(gc: LinhaGiftCards, agora: Date = new Date()): StatusParcela {
  if (gc.resgatado_em) return 'pago';
  return gc.validade && new Date(gc.validade + 'T00:00:00') < agora ? 'atrasada' : 'pendente';
}

/* ---------------------------------------------------------------------------------------------
 * PARCELAS DO MODAL "REGISTRAR PAGAMENTO"
 * ------------------------------------------------------------------------------------------- */

/** Uma parcela prevista: número, valor e data de vencimento. */
export interface ParcelaPrevista {
  readonly numero: number;
  readonly valor: number;
  readonly data: Date;
}

/**
 * As parcelas de um plano (`calcularParcelasPreview`, `admin.html:8007`): à vista, uma parcela com o
 * total; parcelado, a 1ª é a entrada e as outras dividem o resto igualmente, a cada 30 dias.
 * SEM arredondamento: o valor é gravado com `toFixed(2)` só na hora de salvar, como no legado.
 */
export function calcularParcelas(total: number, qtd: number, entrada: number, dataBase: Date): ParcelaPrevista[] {
  const resultado: ParcelaPrevista[] = [];
  if (qtd <= 1) {
    resultado.push({ numero: 1, valor: total, data: new Date(dataBase) });
  } else {
    const valEntrada = entrada > 0 ? entrada : 0;
    const restante = Math.max(0, total - valEntrada);
    const valDemais = qtd > 1 ? restante / (qtd - 1) : 0;
    for (let i = 0; i < qtd; i++) {
      const d = new Date(dataBase);
      // 30 dias por parcela, pelo calendário local, como o `setDate` do legado.
      d.setDate(d.getDate() + i * 30);
      resultado.push({ numero: i + 1, valor: i === 0 ? valEntrada : valDemais, data: d });
    }
  }
  return resultado;
}

/** A data AAAA-MM-DD do campo como data local, como o `split('-')` do legado (`admin.html:8042`). */
export function dataDoCampo(dataStr: string): Date {
  const [ano, mes, dia] = dataStr.split('-').map(Number);
  return new Date(ano ?? 0, (mes ?? 1) - 1, dia ?? 1);
}

/* ---------------------------------------------------------------------------------------------
 * RELATÓRIO (`montarHTMLRelatorio`, `admin.html:8640` a `8741`)
 * ------------------------------------------------------------------------------------------- */

/** O agrupamento escolhido no modal. */
export type Agrupamento = 'mes' | 'cliente' | 'status';

/** O que o relatório recebe, como as `opts` do legado. */
export interface DadosDoRelatorio {
  readonly planos: readonly PlanoFinanceiro[];
  readonly agrupamento: Agrupamento;
  readonly filtros: FiltrosFinanceiro;
  readonly totais: TotaisDoResumo;
  readonly giftCards: readonly LinhaGiftCards[];
  readonly cupons: readonly LinhaCuponsDescontoResgates[];
  readonly leads: readonly LinhaLeadsFunil[];
  // As cores do tema claro, lidas dos tokens pela tela (ver `coresDoRelatorio` em `relatorio.ts`).
  readonly cores: string;
  readonly agora?: Date;
}

/**
 * Texto no HTML do relatório, com os cinco caracteres especiais escapados.
 *
 * DIFERENÇA DECLARADA: o legado põe nome de cliente e de gift card no HTML SEM escapar, e um nome
 * com "<" vira marcação na janela do relatório (injeção de HTML). Com nome comum o resultado é
 * idêntico; com "<", aqui ele aparece como texto. Registrado em PARIDADE-A5 como achado de
 * segurança, que não entra reproduzido.
 */
function esc(texto: string | number | null | undefined): string {
  return String(texto ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** As linhas de parcela do detalhamento (`linhasParcelasHTML`, `admin.html:8629`). */
function linhasDeParcelas(
  lista: readonly (LinhaParcelas & { readonly _nomeCliente?: string })[],
  agora: Date,
): string {
  // A cor de cada estado, pelos tokens: pago em sucesso, atrasada em erro, pendente em texto fraco.
  const classe: Record<StatusParcela, string> = { pago: 'st-pago', atrasada: 'st-atrasada', pendente: 'st-pendente' };
  return lista
    .map((p) => {
      const s = statusParcela(p, agora);
      const venc = new Date(p.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR');
      const pagoEm = p.data_pagamento ? new Date(p.data_pagamento + 'T00:00:00').toLocaleDateString('pt-BR') : '-';
      return `<tr><td>${esc(p._nomeCliente || '-')}</td><td>${p.numero}</td><td>${formatarMoeda(p.valor)}</td><td>${venc}</td><td>${pagoEm}</td><td class="${classe[s]}">${ROTULO_STATUS_PARCELA[s]}</td></tr>`;
    })
    .join('');
}

/** O documento inteiro do relatório, pronto para `document.write`. */
export function montarHtmlRelatorio(dados: DadosDoRelatorio): string {
  const agora = dados.agora ?? new Date();
  const { dataIni, dataFim } = limitesDeData(dados.filtros);
  const { dataIni: dataIniStr, dataFim: dataFimStr } = dados.filtros;
  // O período escrito como o legado: "início" e "hoje" nas pontas abertas, e "Todo o período" sem filtro.
  const periodo =
    dataIniStr || dataFimStr
      ? `${dataIniStr ? new Date(dataIniStr + 'T00:00:00').toLocaleDateString('pt-BR') : 'início'} a ${dataFimStr ? new Date(dataFimStr + 'T00:00:00').toLocaleDateString('pt-BR') : 'hoje'}`
      : 'Todo o período';

  // Todas as parcelas, com o nome da cliente junto, na ordem dos planos.
  const todasParcelas = dados.planos.flatMap((pag) =>
    pag.parcelas.map((p) => ({ ...p, _nomeCliente: pag.lead?.nome || '-' })),
  );
  const noIntervalo = (p: Pick<LinhaParcelas, 'data_vencimento'>) => parcelaNoIntervalo(p, dataIni, dataFim);
  let corpo = '';

  if (dados.agrupamento === 'mes') {
    // Por mês de vencimento, em ordem de mês (a chave AAAA-MM ordena como texto).
    const porMes: Record<string, typeof todasParcelas> = {};
    todasParcelas.filter(noIntervalo).forEach((p) => {
      const d = new Date(p.data_vencimento + 'T00:00:00');
      const chave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      (porMes[chave] ??= []).push(p);
    });
    Object.keys(porMes)
      .sort()
      .forEach((chave) => {
        const [ano, mes] = chave.split('-');
        const grupo = porMes[chave] ?? [];
        const rec = grupo.filter((p) => p.pago).reduce((s, p) => s + p.valor, 0);
        const ar = grupo.filter((p) => !p.pago).reduce((s, p) => s + p.valor, 0);
        corpo += `<tr class="grupo"><td colspan="6">${MESES_NOMES[parseInt(mes ?? '1', 10) - 1]} ${ano} · Recebido: ${formatarMoeda(rec)} · A receber: ${formatarMoeda(ar)}</td></tr>`;
        corpo += linhasDeParcelas(grupo, agora);
      });
  } else if (dados.agrupamento === 'cliente') {
    // Por plano, na ordem da lista, com o valor do contrato.
    dados.planos.forEach((pag) => {
      const parcFiltradas = pag.parcelas.filter(noIntervalo);
      if (!parcFiltradas.length) return;
      const rec = parcFiltradas.filter((p) => p.pago).reduce((s, p) => s + p.valor, 0);
      const ar = parcFiltradas.filter((p) => !p.pago).reduce((s, p) => s + p.valor, 0);
      corpo += `<tr class="grupo"><td colspan="6">${esc(pag.lead?.nome || '(sem nome)')} · Contrato: ${formatarMoeda(pag.valor_total)} · Recebido: ${formatarMoeda(rec)} · A receber: ${formatarMoeda(ar)}</td></tr>`;
      corpo += linhasDeParcelas(parcFiltradas.map((p) => ({ ...p, _nomeCliente: pag.lead?.nome || '-' })), agora);
    });
  } else {
    // Por estado, nesta ordem: atrasadas, pendentes, pagas; grupo vazio não aparece.
    const grupos: Record<StatusParcela, typeof todasParcelas> = { atrasada: [], pendente: [], pago: [] };
    todasParcelas.filter(noIntervalo).forEach((p) => grupos[statusParcela(p, agora)].push(p));
    const rotulos: Record<StatusParcela, string> = { atrasada: 'Atrasadas', pendente: 'Pendentes', pago: 'Pagas' };
    (['atrasada', 'pendente', 'pago'] as const).forEach((s) => {
      if (!grupos[s].length) return;
      const total = grupos[s].reduce((acc, p) => acc + p.valor, 0);
      corpo += `<tr class="grupo"><td colspan="6">${rotulos[s]} · ${formatarMoeda(total)}</td></tr>`;
      corpo += linhasDeParcelas(grupos[s], agora);
    });
  }

  // O resumo por cliente, só de quem tem parcela no período.
  let resumoClientes = '';
  dados.planos.forEach((pag) => {
    const parcFiltradas = pag.parcelas.filter(noIntervalo);
    if (!parcFiltradas.length) return;
    const rec = parcFiltradas.filter((p) => p.pago).reduce((s, p) => s + p.valor, 0);
    const ar = parcFiltradas.filter((p) => !p.pago).reduce((s, p) => s + p.valor, 0);
    resumoClientes += `<tr><td>${esc(pag.lead?.nome || '(sem nome)')}</td><td>${formatarMoeda(pag.valor_total)}</td><td class="st-pago">${formatarMoeda(rec)}</td><td class="st-atrasada">${formatarMoeda(ar)}</td></tr>`;
  });

  const agrupLabels: Record<Agrupamento, string> = { mes: 'por mês', cliente: 'por cliente', status: 'por status' };

  // Os gift cards do período, com a situação escrita como o legado escreve NO RELATÓRIO (`8706`).
  const linhasGiftCards = dados.giftCards
    .map((gc) => {
      const status = gc.resgatado_em
        ? `Resgatado (${new Date(gc.resgatado_em).toLocaleDateString('pt-BR')})`
        : gc.validade && new Date(gc.validade + 'T00:00:00') < agora
          ? 'Expirado'
          : 'Ativo';
      const emitidoFmt = new Date(gc.emitido_em).toLocaleDateString('pt-BR');
      return `<tr><td>${esc(gc.codigo)}</td><td>${esc(gc.nome_comprador || '-')}</td><td>${esc(gc.nome_destinatario || '-')}</td><td>${formatarMoeda(Number(gc.valor))}</td><td>${emitidoFmt}</td><td>${status}</td></tr>`;
    })
    .join('');
  const secaoGiftCards = dados.giftCards.length
    ? `<h2>Gift Cards emitidos</h2><table><thead><tr><th>Código</th><th>Comprador</th><th>Destinatário</th><th>Valor</th><th>Emitido em</th><th>Status</th></tr></thead><tbody>${linhasGiftCards}</tbody></table>`
    : `<h2>Gift Cards emitidos</h2><p class="sub">Nenhum gift card emitido nesse período.</p>`;

  // Os cupons do período; lead que sumiu aparece como "-" no relatório (`8715`).
  const linhasCupons = dados.cupons
    .map((r) => {
      const leadNome = nomeDoLead(dados.leads, r.lead_id) || '-';
      const aplicadoFmt = new Date(r.aplicado_em ?? '').toLocaleDateString('pt-BR');
      return `<tr><td>${esc(r.codigo)}</td><td>${esc(leadNome)}</td><td class="st-atrasada">-${formatarMoeda(Number(r.valor_aplicado))}</td><td>${aplicadoFmt}</td></tr>`;
    })
    .join('');
  const secaoCupons = dados.cupons.length
    ? `<h2>Cupons utilizados</h2><table><thead><tr><th>Código</th><th>Cliente</th><th>Desconto</th><th>Aplicado em</th></tr></thead><tbody>${linhasCupons}</tbody></table>`
    : `<h2>Cupons utilizados</h2><p class="sub">Nenhum cupom utilizado nesse período.</p>`;

  const { recebido, aReceber, descontos } = dados.totais;
  // O estilo do legado (`admin.html:8724`), com cada cor lida dos tokens por `var(--cx-*)`: as
  // variáveis são declaradas no topo, com os valores do tema claro que a tela leu.
  const estilo =
    `${dados.cores}` +
    'body{font-family:Arial,sans-serif;font-size:13px;color:rgb(var(--cx-texto));margin:32px;}' +
    'h1{font-size:20px;margin-bottom:4px;}' +
    '.sub{color:rgb(var(--cx-texto-suave));font-size:12px;margin-bottom:20px;}' +
    '.resumo-box{display:flex;gap:40px;padding:14px 18px;border:1px solid rgb(var(--cx-borda));border-radius:4px;margin-bottom:24px;}' +
    '.resumo-item{display:flex;flex-direction:column;gap:2px;}' +
    '.resumo-label{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:rgb(var(--cx-texto-fraco));font-weight:600;}' +
    '.resumo-valor{font-size:16px;font-weight:700;}' +
    '.verde,.st-pago{color:rgb(var(--cx-sucesso));}' +
    '.vermelho,.st-atrasada{color:rgb(var(--cx-erro));}' +
    '.st-pendente{color:rgb(var(--cx-texto-fraco));}' +
    '.st-pago,.st-atrasada,.st-pendente{font-weight:600;}' +
    'h2{font-size:14px;font-weight:600;margin:28px 0 10px;}' +
    'table{width:100%;border-collapse:collapse;}' +
    'th{text-align:left;padding:8px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:rgb(var(--cx-texto-suave));border-bottom:2px solid rgb(var(--cx-borda));}' +
    'td{padding:8px;border-bottom:1px solid rgb(var(--cx-borda) / 0.45);}' +
    'tr.grupo{background:rgb(var(--cx-superficie));}' +
    'tr.grupo td{font-weight:600;padding:10px 8px;}' +
    '@media print{body{margin:16px;}}';

  return `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>Fluxo de Caixa · Lu Barros Studio</title>
<style>${estilo}</style>
</head><body>
<h1>Fluxo de Caixa · Lu Barros Studio</h1>
<p class="sub">Gerado em ${agora.toLocaleString('pt-BR')} · Período: ${periodo} · Agrupamento: ${agrupLabels[dados.agrupamento]}</p>
<div class="resumo-box">
  <div class="resumo-item"><span class="resumo-label">Recebido</span><span class="resumo-valor verde">${formatarMoeda(recebido)}</span></div>
  <div class="resumo-item"><span class="resumo-label">A receber</span><span class="resumo-valor vermelho">${formatarMoeda(aReceber)}</span></div>
  <div class="resumo-item"><span class="resumo-label">Total geral</span><span class="resumo-valor">${formatarMoeda(recebido + aReceber)}</span></div>
  <div class="resumo-item"><span class="resumo-label">Descontos em cupons</span><span class="resumo-valor vermelho">${formatarMoeda(descontos)}</span></div>
</div>
<h2>Detalhamento · ${agrupLabels[dados.agrupamento]}</h2>
<table><thead><tr><th>Cliente</th><th>Parc.</th><th>Valor</th><th>Vencimento</th><th>Pago em</th><th>Status</th></tr></thead><tbody>${corpo}</tbody></table>
<h2>Resumo por cliente</h2>
<table><thead><tr><th>Cliente</th><th>Contrato</th><th class="st-pago">Recebido</th><th class="st-atrasada">A receber</th></tr></thead><tbody>${resumoClientes}</tbody></table>
${secaoGiftCards}
${secaoCupons}
</body></html>`;
}
